// web/src/screens/LoginScreen.jsx
import { useState, useEffect } from 'react';
import { apiClient } from '../api';

const C = {
  bg: 'var(--bg)', surface: 'var(--surface)', orange: 'var(--orange)',
  text: 'var(--text)', muted: 'var(--text-muted)', danger: 'var(--danger)',
};

export default function LoginScreen({ onLogin }) {
  const [phone, setPhone]       = useState('');
  const [pin, setPin]           = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // Venue resolution from URL slug
  const [slug, setSlug]             = useState('');
  const [venue, setVenue]           = useState(null);
  const [venueLoading, setVenueLoading] = useState(true);
  const [venueError, setVenueError]     = useState('');

  // On mount — extract slug from URL path: /v/test-bar-3
  useEffect(() => {
    const parts = window.location.pathname.split('/');
    const vIndex = parts.indexOf('v');
    const extractedSlug = vIndex !== -1 ? parts[vIndex + 1] : '';

    if (!extractedSlug) {
      setVenueLoading(false);
      setVenueError('No venue in this link. Scan your QR code or ask your manager for the correct link.');
      return;
    }

    setSlug(extractedSlug);

    apiClient.get(`/v/${extractedSlug}`)
      .then(({ data }) => {
        if (!data.active) {
          setVenueError('This venue subscription is inactive. Contact your manager.');
        } else {
          setVenue(data);
        }
      })
      .catch(() => {
        setVenueError('Venue not found. Scan your QR code or ask your manager for the correct link.');
      })
      .finally(() => setVenueLoading(false));
  }, []);

  const handleLogin = async () => {
    if (!slug || !venue) return;
    if (phone.length < 10) return setError('Enter a valid phone number');
    if (pin.length !== 4)  return setError('PIN must be 4 digits');

    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.post('/auth/login', { phone, pin, slug });
      onLogin(data.accessToken, { name: data.name, role: data.role }, data.force_pin_change, pin);
    } catch (err) {
      setError(err.data?.error || 'Login failed. Check your phone and PIN.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handlePinKey = (key) => {
    if (key === 'DEL') return setPin(p => p.slice(0, -1));
    if (pin.length < 4) setPin(p => p + key);
  };

  // Keyboard support — digits go to PIN, Backspace deletes, Enter submits.
  // Skipped when the phone input is focused so normal number typing works.
  useEffect(() => {
    const onKey = (e) => {
      const phoneInputFocused = document.activeElement?.type === 'tel';
      if (phoneInputFocused) return;

      if (/^\d$/.test(e.key)) {
        setPin(p => p.length < 4 ? p + e.key : p);
      } else if (e.key === 'Backspace') {
        setPin(p => p.slice(0, -1));
      } else if (e.key === 'Enter') {
        handleLogin();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pin, slug, venue, phone, loading]); // deps keep handleLogin closure fresh

  // ── Loading venue ──────────────────────────────────────────────
  if (venueLoading) {
    return (
      <div style={{ ...s.container, backgroundColor: C.bg }}>
        <div style={s.card}>
          <h1 style={s.logo}>ServeIQ</h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 16 }}>Loading venue…</p>
        </div>
      </div>
    );
  }

  // ── Venue not found ────────────────────────────────────────────
  if (venueError) {
    return (
      <div style={{ ...s.container, backgroundColor: C.bg }}>
        <div style={s.card}>
          <h1 style={s.logo}>ServeIQ</h1>
          <div style={s.venueErrorBox}>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>{venueError}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal login ───────────────────────────────────────────────
  return (
    <div style={{ ...s.container, backgroundColor: C.bg }}>
      <div style={s.card}>
        <h1 style={s.logo}>ServeIQ</h1>

        {/* Venue badge — short_name keeps it readable on small screens */}
        <div style={s.venueBadge} title={venue.venue_name}>
          <span style={s.venueType}>{venue.venue_type?.toUpperCase()}</span>
          <span style={s.venueName}>{venue.short_name}</span>
        </div>

        <input
          style={s.input}
          type="tel"
          placeholder="+254 7XX XXX XXX"
          value={phone}
          onChange={e => setPhone(e.target.value)}
        />

        {/* PIN dots */}
        <div style={s.pinDots}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ ...s.dot, backgroundColor: pin.length > i ? C.orange : C.surface }} />
          ))}
        </div>

        {/* Numpad */}
        <div style={s.numpad}>
          {['1','2','3','4','5','6','7','8','9','','0','DEL'].map((key, i) => (
            <button
              key={i}
              style={{ ...s.numKey, opacity: key === '' ? 0 : 1 }}
              onClick={() => key && handlePinKey(key)}
              disabled={!key}
            >
              {key}
            </button>
          ))}
        </div>

        {error && <p style={s.error}>{error}</p>}

        <button
          style={{ ...s.loginBtn, opacity: (loading || !venue) ? 0.7 : 1 }}
          onClick={handleLogin}
          disabled={loading || !venue}
        >
          {loading ? 'Signing in…' : 'SIGN IN'}
        </button>
      </div>
    </div>
  );
}

const s = {
  container:    { display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' },
  card:         { background:'var(--surface)', borderRadius:12, padding:32, width:'100%', maxWidth:360, textAlign:'center' },
  logo:         { color:'var(--orange)', fontSize:36, fontWeight:700, margin:'0 0 4px', letterSpacing:2 },

  venueBadge:   { display:'flex', flexDirection:'column', alignItems:'center', gap:2,
                  background:'var(--surface-2)', borderRadius:8, padding:'10px 16px', marginBottom:20,
                  border:'1px solid var(--border)' },
  venueType:    { color:'var(--orange)', fontSize:10, fontWeight:700, letterSpacing:2 },
  venueName:    { color:'var(--text)', fontSize:18, fontWeight:700, letterSpacing:0.5 },

  venueErrorBox:{ background:'var(--surface-2)', border:'1px solid var(--danger)', borderRadius:8,
                  padding:'14px 16px', marginTop:20, color:'var(--danger)' },

  input:        { width:'100%', padding:'12px 16px', background:'var(--surface-2)', border:'1px solid var(--border)',
                  borderRadius:8, color:'var(--text)', fontSize:16, marginBottom:20, boxSizing:'border-box' },
  pinDots:      { display:'flex', gap:12, justifyContent:'center', marginBottom:20 },
  dot:          { width:16, height:16, borderRadius:'50%', border:'2px solid var(--orange)', transition:'background 0.15s' },
  numpad:       { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, marginBottom:16 },
  numKey:       { padding:'16px 0', background:'var(--surface-2)', border:'none', borderRadius:8,
                  color:'var(--text)', fontSize:20, fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  error:        { color:'var(--danger)', fontSize:13, marginBottom:12 },
  loginBtn:     { width:'100%', padding:'14px 0', background:'var(--orange)', border:'none', borderRadius:8,
                  color:'#fff', fontSize:18, fontWeight:700, cursor:'pointer', letterSpacing:1 },
};
